"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import Image from "next/image";
import ReactPlayer from "react-player";
import styles from "./event.module.css";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  IconPlayerPlayFilled,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";

// react-player has no Instagram provider (Mux/YouTube/Vimeo/Wistia/Spotify/
// Twitch/TikTok/direct-file only) — for anything else it falls back to a
// plain <video src>, which can't play a reel page URL and trips the
// media-src CSP directive. Route Instagram links to their official iframe
// embed instead, same as how YouTube already goes through frame-src.
const INSTAGRAM_URL_PATTERN = /instagram\.com\/(reel|reels|p|tv)\/([^/?#]+)/i;

// Cross-component signal so the event banner and the Gen Z reels (GenZSection.js)
// never play at once — whichever starts tells the other to stop, independent of scroll.
const MEDIA_PLAYBACK_EVENT = "tykkit-media-playback";
const MEDIA_SOURCE = "event-banner";

function getInstagramEmbedUrl(url) {
  const match =
    typeof url === "string" ? url.match(INSTAGRAM_URL_PATTERN) : null;
  if (!match) return null;
  const [, type, shortcode] = match;
  return `https://www.instagram.com/${type === "reels" ? "reel" : type}/${shortcode}/embed`;
}

function getYouTubeThumbnail(url) {
  if (!url || typeof url !== "string") return null;
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/,
  );
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
}

export default function BannerWithVideo({
  img,
  video,
  thumbnail,
  name = "event",
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);
  const instagramEmbedUrl = getInstagramEmbedUrl(video);
  const bannerRef = useRef(null);
  const videoWasStartedRef = useRef(false);

  const videoPosterUrl = thumbnail || getYouTubeThumbnail(video) || img;

  const stopBannerVideo = useCallback(() => {
    setIsPlaying(false);
    if (!videoWasStartedRef.current) return;

    videoWasStartedRef.current = false;

    if (bannerRef.current) {
      const videos = bannerRef.current.querySelectorAll("video");
      videos.forEach((v) => {
        try {
          v.pause();
        } catch (e) {
          // ignore
        }
      });

      const iframes = bannerRef.current.querySelectorAll("iframe");
      iframes.forEach((iframe) => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              JSON.stringify({
                event: "command",
                func: "pauseVideo",
                args: "",
              }),
              "*",
            );
            iframe.contentWindow.postMessage(
              JSON.stringify({
                event: "command",
                func: "stopVideo",
                args: "",
              }),
              "*",
            );
          }
        } catch (e) {
          // ignore
        }

        try {
          const currentSrc = iframe.getAttribute("src") || iframe.src;
          if (currentSrc) {
            iframe.src = currentSrc;
          }
        } catch (e) {
          // ignore
        }
      });
    }

    if (document.activeElement && document.activeElement.tagName === "IFRAME") {
      document.activeElement.blur();
    }
  }, []);

  const markVideoStarted = useCallback(() => {
    videoWasStartedRef.current = true;
    setIsPlaying(true);
    window.dispatchEvent(
      new CustomEvent(MEDIA_PLAYBACK_EVENT, {
        detail: { source: MEDIA_SOURCE },
      }),
    );
  }, []);

  useEffect(() => {
    const handleBlur = () => {
      setTimeout(() => {
        if (
          document.activeElement &&
          document.activeElement.tagName === "IFRAME" &&
          bannerRef.current?.contains(document.activeElement)
        ) {
          videoWasStartedRef.current = true;
          window.dispatchEvent(
            new CustomEvent(MEDIA_PLAYBACK_EVENT, {
              detail: { source: MEDIA_SOURCE },
            }),
          );
        }
      }, 100);
    };

    const handlePointerDown = (e) => {
      if (bannerRef.current?.contains(e.target)) {
        videoWasStartedRef.current = true;
      }
    };

    window.addEventListener("blur", handleBlur);
    window.addEventListener("pointerdown", handlePointerDown, {
      passive: true,
    });
    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    const handleExternalMediaPlay = (e) => {
      if (e.detail?.source !== MEDIA_SOURCE) {
        stopBannerVideo();
      }
    };

    window.addEventListener(MEDIA_PLAYBACK_EVENT, handleExternalMediaPlay);
    return () =>
      window.removeEventListener(MEDIA_PLAYBACK_EVENT, handleExternalMediaPlay);
  }, [stopBannerVideo]);

  // IntersectionObserver replaces the old scroll listener: it doesn't run on
  // every scroll frame, and it directly answers "is the banner still visible"
  // instead of approximating that from a scrollY delta.
  useEffect(() => {
    if (!bannerRef.current || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && videoWasStartedRef.current) {
          stopBannerVideo();
        }
      },
      { threshold: 0 },
    );

    observer.observe(bannerRef.current);
    return () => observer.disconnect();
  }, [stopBannerVideo]);

  // When video is playing, scrolling the page immediately stops video and restores poster
  useEffect(() => {
    if (!isPlaying) return;

    const initialY = typeof window !== "undefined" ? window.scrollY : 0;

    const handleScroll = () => {
      const currentY = window.scrollY;
      if (Math.abs(currentY - initialY) > 20) {
        stopBannerVideo();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isPlaying, stopBannerVideo]);

  // Tab switched away or minimized — pause rather than let it keep playing
  // (and keep making sound) off-screen.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && videoWasStartedRef.current) {
        stopBannerVideo();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [stopBannerVideo]);

  if ([null, undefined].includes(video)) {
    return (
      <div
        className={`${styles.cover} `}
        style={{
          borderRadius: "16px",
          marginBottom: "24px",
        }}
      >
        <Image
          priority
          src={img}
          width={1000}
          height={500}
          sizes="(max-width: 768px) 100vw, 1000px"
          placeholder="blur"
          blurDataURL={thumbnail}
          alt={name || "event"}
        />
      </div>
    );
  }

  return (
    <>
      <div ref={bannerRef} className={styles.bannerWrapper}>
        {/* Nav arrows — opacity:0 by default, shown on hover via CSS */}
        {activeIndex > 0 && (
          <button
            type="button"
            aria-label="Previous slide"
            className={`${styles.bannerNavBtn} ${styles.bannerNavPrev}`}
            onClick={() => {
              swiperRef.current?.slideTo(0);
              stopBannerVideo();
            }}
          >
            <IconChevronLeft size={22} />
          </button>
        )}
        {activeIndex < 1 && (
          <button
            type="button"
            aria-label="Next slide"
            className={`${styles.bannerNavBtn} ${styles.bannerNavNext}`}
            onClick={() => swiperRef.current?.slideTo(1)}
          >
            <IconChevronRight size={22} />
          </button>
        )}

        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex);
            if (swiper.activeIndex === 1) {
              markVideoStarted();
            } else {
              stopBannerVideo();
            }
          }}
          onSlideNextStart={stopBannerVideo}
          onSlidePrevStart={stopBannerVideo}
          onTouchStart={stopBannerVideo}
          className={styles.bannerSwiper}
        >
          <SwiperSlide>
            <div
              className={styles.cover}
              style={{
                borderRadius: "16px",
              }}
            >
              <Image
                priority
                src={img}
                width={1000}
                height={500}
                sizes="(max-width: 768px) 100vw, 1000px"
                placeholder="blur"
                blurDataURL={thumbnail}
                alt={name || "event"}
              />
            </div>
          </SwiperSlide>

          <SwiperSlide>
            <div
              style={{
                borderRadius: "16px",
              }}
              className={`${styles.cover} ${
                instagramEmbedUrl ? styles.instgramCover : ""
              }`}
            >
              {!isPlaying ? (
                <div
                  className={styles.videoPosterWrapper}
                  onClick={markVideoStarted}
                  role="button"
                  tabIndex={0}
                  style={{
                    borderRadius: "16px",
                  }}
                >
                  <Image
                    src={videoPosterUrl}
                    width={1000}
                    height={500}
                    sizes="(max-width: 768px) 100vw, 1000px"
                    placeholder="blur"
                    blurDataURL={thumbnail || img}
                    alt={name || "event video"}
                    className="w-full h-full object-cover"
                  />
                  <div className={styles.videoPosterOverlay}>
                    <div className={styles.playButtonCircle}>
                      <IconPlayerPlayFilled size={26} className="text-white" />
                    </div>
                    <span className={styles.watchVideoText}>Watch Video</span>
                  </div>
                </div>
              ) : instagramEmbedUrl ? (
                <iframe
                  src={instagramEmbedUrl}
                  title={`${name} Instagram reel preview`}
                  className={styles.instagramIframe}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                  scrolling="no"
                />
              ) : (
                <ReactPlayer
                  src={video}
                  onPlay={markVideoStarted}
                  onPause={() => {
                    setIsPlaying(false);
                  }}
                  onEnded={() => {
                    setIsPlaying(false);
                    videoWasStartedRef.current = false;
                  }}
                  playing={true}
                  config={{
                    youtube: {
                      playerVars: {
                        autoplay: 1,
                        enablejsapi: 1,
                        origin:
                          typeof window !== "undefined"
                            ? window.location.origin
                            : "",
                      },
                    },
                  }}
                  style={{ width: "100%", height: "100%", aspectRatio: "2/1" }}
                  width="100%"
                  height="100%"
                />
              )}
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      {/* Pagination dots — below the banner in normal flow */}
      <div className={styles.bannerCustomPagination}>
        <button
          type="button"
          aria-label="Go to banner image"
          onClick={() => {
            swiperRef.current?.slideTo(0);
            stopBannerVideo();
          }}
          className={`${styles.bannerBullet} ${
            activeIndex === 0 ? styles.bannerBulletActive : ""
          }`}
        />
        <button
          type="button"
          aria-label="Go to video"
          onClick={() => swiperRef.current?.slideTo(1)}
          className={`${styles.bannerBullet} ${
            activeIndex === 1 ? styles.bannerBulletActive : ""
          }`}
        />
      </div>
    </>
  );
}
